import React, { useState } from 'react';
import { Project, Product, KeywordWithIntent, IntentCategoryConfig, ViewType } from '../types';
import FileUpload from './FileUpload';
import ViewTypeSelectionModal from './ViewTypeSelectionModal';

interface EditProjectModalProps {
  project: Project;
  onClose: () => void;
  onProjectUpdated: (project: Project) => void;
}

interface ProcessedKeywordData {
  productName?: string;
  productCategory: string;
  keywords: KeywordWithIntent[];
  ingredients?: string;
  legalClaims?: string;
  copy?: string;
  brand?: string;
  subBrand?: string;
}

const EditProjectModal: React.FC<EditProjectModalProps> = ({ project, onClose, onProjectUpdated }) => {
  const [projectName, setProjectName] = useState(project.name);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [showViewTypeSelection, setShowViewTypeSelection] = useState(false);
  const [availableViewTypes, setAvailableViewTypes] = useState<ViewType[]>([]);
  const [pendingData, setPendingData] = useState<{
    data: ProcessedKeywordData[];
    intentCategories: string[];
    intentCategoryConfig: Record<string, IntentCategoryConfig>;
    fileInfo: { name: string; size: number; data: string };
  } | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleSaveName = () => {
    if (!projectName.trim()) {
      alert('Please enter a project name');
      return;
    }

    setIsSaving(true);

    const updatedProject: Project = {
      ...project,
      name: projectName.trim(),
      updatedAt: new Date()
    };

    onProjectUpdated(updatedProject);
    setIsSaving(false);
    onClose();
  };

  const handleImportData = (
    importedData: ProcessedKeywordData[], 
    intentCategories: string[], 
    intentCategoryConfig: Record<string, IntentCategoryConfig>,
    availableViewTypes: ViewType[],
    fileInfo: { name: string; size: number; data: string }
  ) => {
    if (!projectName.trim()) {
      alert('Please enter a project name');
      return;
    }

    // Store data and show view type selection
    setPendingData({ data: importedData, intentCategories, intentCategoryConfig, fileInfo });
    setAvailableViewTypes(availableViewTypes);
    setShowViewTypeSelection(true);
  };

  const handleViewTypeSelected = (viewType: ViewType) => {
    if (!pendingData) return;

    const { data: importedData, intentCategories, intentCategoryConfig, fileInfo } = pendingData;

    // Create products from imported data
    const products: Product[] = importedData.map((data, index) => ({
      id: `product-${Date.now()}-${index}`,
      name: data.productName || data.brand || data.subBrand || 'Unnamed',
      description: '',
      category: data.productCategory,
      keywords: data.keywords,
      selectedKeywords: [],
      status: 'pending' as const,
      ingredients: data.ingredients,
      legalClaims: data.legalClaims,
      copy: data.copy,
      brand: data.brand,
      subBrand: data.subBrand
    }));

    // Update project with imported data
    const updatedProject: Project = {
      ...project,
      name: projectName.trim(),
      description: `Project with ${products.length} items imported from Excel`,
      products: products,
      totalProducts: products.length,
      updatedAt: new Date(),
      intentCategories: intentCategories.length > 0 ? intentCategories : undefined,
      intentCategoryConfig: Object.keys(intentCategoryConfig).length > 0 ? intentCategoryConfig : undefined,
      viewType: viewType,
      originalFile: {
        name: fileInfo.name,
        size: fileInfo.size,
        uploadedAt: new Date(),
        data: fileInfo.data
      }
    };

    onProjectUpdated(updatedProject);
    onClose();
  };

  const handleViewTypeCancel = () => {
    setShowViewTypeSelection(false);
    setPendingData(null);
  };

  const handleFileUploadClose = () => {
    setShowFileUpload(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-primary-900">Edit Project</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="projectName" className="block text-sm font-medium text-gray-700 mb-2">
              Project Name
            </label>
            <input
              id="projectName"
              type="text"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="Enter project name..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
            />
          </div>

          <div className="space-y-3">
            <button
              onClick={handleSaveName}
              disabled={isSaving || projectName.trim() === project.name}
              className="w-full px-4 py-3 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">or</span>
              </div>
            </div>

            <button
              onClick={() => setShowFileUpload(true)}
              className="w-full px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Re-upload Excel File
            </button>
          </div>

          <div className="text-xs text-gray-500 text-center">
            Re-uploading a file will replace all existing products and keywords
          </div>
        </div>

        {/* File Upload Modal */}
        {showFileUpload && (
          <FileUpload
            onDataProcessed={handleImportData}
            onClose={handleFileUploadClose}
          />
        )}

        {/* View Type Selection Modal */}
        {showViewTypeSelection && (
          <ViewTypeSelectionModal
            availableViewTypes={availableViewTypes}
            onViewTypeSelected={handleViewTypeSelected}
            onCancel={handleViewTypeCancel}
          />
        )}
      </div>
    </div>
  );
};

export default EditProjectModal;

